import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, AddressBook, ClipboardText, Receipt, IdentificationCard, Buildings, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CLIENT_TYPES, CLIENT_CATEGORIES, KYC_STATUS, JOB_STATUS_LABELS, JOB_STATUS_COLORS, INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS, PROPERTY_TYPES, inr } from "@/lib/constants";
import { SectionTitle, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => api.get(`/clients/${id}/profile`).then((r) => { setData(r.data); setForm(r.data.client); }).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (extra = {}) => {
    try {
      await api.put(`/clients/${id}`, { ...form, ...extra });
      toast.success("Client updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (data === false) return <EmptyState icon={AddressBook}>Client not found. <Link to="/app/clients" className="text-accent hover:underline">Back to all clients</Link></EmptyState>;
  if (!data || !form) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading client profile…</div>;

  const c = data.client;

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <Link to="/app/clients" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors duration-150" data-testid="back-to-clients">
        <ArrowLeft size={15} /> All Clients
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Client Profile</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">{c.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{c.client_type}{c.category ? ` · ${c.category}` : ""}{c.group_name ? ` · Group: ${c.group_name}` : ""}{c.city ? ` · ${c.city}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(c.tags || []).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border bg-accent/10 text-accent border-accent/20">{t}</span>)}
          <Button onClick={() => navigate(`/app/jobs?new=1&client=${id}&from=${id}`)} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="client-new-job-btn"><Plus size={16} className="mr-1" /> New Valuation Job</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Valuations" value={data.jobs.length} testid="stat-client-jobs" />
        <Stat label="Billed" value={inr(data.billed)} testid="stat-client-billed" />
        <Stat label="Paid" value={inr(data.paid)} tone="text-emerald-600" testid="stat-client-paid" />
        <Stat label="Outstanding" value={inr(data.outstanding)} tone="text-amber-600" testid="stat-client-outstanding" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="ctab-overview">Overview</TabsTrigger>
          <TabsTrigger value="property" data-testid="ctab-property">Property</TabsTrigger>
          <TabsTrigger value="history" data-testid="ctab-history">Valuation History</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="ctab-invoices">Invoices & Payments</TabsTrigger>
          <TabsTrigger value="tags" data-testid="ctab-tags">Categories & Group</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="p-6 border-border space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name || ""} onChange={set("name")} data-testid="cp-name" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.client_type || "Individual"} onValueChange={set("client_type")}>
                  <SelectTrigger data-testid="cp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact_person || ""} onChange={set("contact_person")} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="cp-phone" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={set("email")} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city || ""} onChange={set("city")} data-testid="cp-city" /></div>
              <div className="space-y-1.5"><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={set("gstin")} /></div>
              <div className="space-y-1.5"><Label>KYC Status</Label>
                <Select value={form.kyc_status || "pending"} onValueChange={set("kyc_status")}>
                  <SelectTrigger data-testid="cp-kyc"><SelectValue /></SelectTrigger>
                  <SelectContent>{KYC_STATUS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={set("address")} /></div>
            <div className="space-y-1.5"><Label>Notes / Communication Log</Label><Textarea rows={4} value={form.notes || ""} onChange={set("notes")} placeholder="Calls, emails and meetings logged here…" data-testid="cp-notes" /></div>
            <Button onClick={() => save()} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-client-profile-btn">Save Changes</Button>
          </Card>
        </TabsContent>

        <TabsContent value="property">
          <Card className="p-6 border-border space-y-3">
            <SectionTitle icon={Buildings}>Property Details</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Property Type</Label>
                <Select value={form.property_type || "Residential"} onValueChange={set("property_type")}>
                  <SelectTrigger data-testid="cp-prop-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Property Address</Label><Textarea rows={3} value={form.property_address || ""} onChange={set("property_address")} data-testid="cp-prop-address" /></div>
            <Button onClick={() => save()} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-property-btn">Save Property</Button>
            <p className="text-xs text-muted-foreground">Additional properties for this client appear automatically as valuation jobs under Valuation History.</p>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="p-6 border-border">
            <div className="flex items-center justify-between gap-3 mb-2">
              <SectionTitle icon={ClipboardText}>Valuation History</SectionTitle>
              <Button onClick={() => navigate(`/app/jobs?new=1&client=${id}&from=${id}`)} size="sm" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="history-new-job-btn"><Plus size={15} className="mr-1" /> New Job</Button>
            </div>
            {data.jobs.length === 0 ? <EmptyState icon={ClipboardText}>No valuations recorded for this client yet. <button onClick={() => navigate(`/app/jobs?new=1&client=${id}&from=${id}`)} className="text-accent hover:underline" data-testid="empty-new-job">Create the first valuation job</button>.</EmptyState> : (
              <div className="space-y-2">
                {data.jobs.map((j) => (
                  <button key={j.id} type="button" onClick={() => navigate(`/app/jobs?edit=${j.id}&from=${id}`)} className="w-full text-left flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted border border-border hover:border-accent hover:bg-muted/60 transition-colors duration-150" data-testid={`cp-job-${j.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary truncate">{j.property_type} · {j.property_address}</div>
                      <div className="text-xs text-muted-foreground font-mono">{j.reference} · {j.bank_name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                      <PencilSimple size={14} className="text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card className="p-6 border-border">
            <div className="flex items-center justify-between gap-3 mb-2">
              <SectionTitle icon={Receipt}>Invoices & Payments</SectionTitle>
              <Button onClick={() => navigate(`/app/invoices?new=1&client=${id}`)} size="sm" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="client-new-invoice-btn"><Plus size={15} className="mr-1" /> Create Invoice</Button>
            </div>
            {data.invoices.length === 0 ? <EmptyState icon={Receipt}>No invoices raised against this client yet. <button onClick={() => navigate(`/app/invoices?new=1&client=${id}`)} className="text-accent hover:underline" data-testid="empty-new-invoice">Create the first invoice</button>.</EmptyState> : (
              <div className="space-y-2">
                {data.invoices.map((i) => (
                  <Link key={i.id} to="/app/invoices" className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted border border-border hover:bg-muted/60 transition-colors duration-150" data-testid={`cp-inv-${i.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary font-mono">{i.invoice_number}</div>
                      <div className="text-xs text-muted-foreground">Issued {(i.issue_date || "").slice(0, 10)}{i.due_date ? ` · due ${i.due_date.slice(0, 10)}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm tabular text-primary">{inr(i.total)}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${INVOICE_STATUS_COLORS[i.status]}`}>{INVOICE_STATUS_LABELS[i.status]}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card className="p-6 border-border space-y-4">
            <SectionTitle icon={IdentificationCard}>Categories & Group</SectionTitle>
            <div className="space-y-1.5 max-w-xs">
              <Label className="mb-2 block">Category</Label>
              <Select value={form.category || ""} onValueChange={(v) => { setForm({ ...form, category: v }); save({ category: v }); }}>
                <SelectTrigger data-testid="cp-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CLIENT_CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">How this engagement was initiated.</p>
            </div>
            <div className="space-y-1.5 max-w-sm">
              <Label>Client Group</Label>
              <Input value={form.group_name || ""} onChange={set("group_name")} placeholder="e.g. Agrawal Family" data-testid="cp-group" />
              <p className="text-xs text-muted-foreground">Group related or family entities under one umbrella — useful for group loans with co-owned properties.</p>
            </div>
            <Button onClick={() => save()} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-tags-btn">Save</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
